// Roles and the permissions each role grants. The server enforces these on
// every request together with organisation, relationship and location scope;
// the web app only uses them to decide what to show.

export const ROLES = [
  'BUYER_ADMIN',
  'BUYER_APPROVER',
  'BUYER_ACCOUNTS',
  'DRIVER',
  'SUPPLIER_ADMIN',
  'SUPPLIER_ACCOUNTS',
  'SUPPLIER_ATTENDANT',
  'PLATFORM_OPERATOR',
] as const;
export type Role = (typeof ROLES)[number];

export const BUYER_ROLES: Role[] = ['BUYER_ADMIN', 'BUYER_APPROVER', 'BUYER_ACCOUNTS', 'DRIVER'];
export const SUPPLIER_ROLES: Role[] = ['SUPPLIER_ADMIN', 'SUPPLIER_ACCOUNTS', 'SUPPLIER_ATTENDANT'];

export const ROLE_LABELS: Record<Role, string> = {
  BUYER_ADMIN: 'Company owner / admin',
  BUYER_APPROVER: 'Fleet manager (approves indents)',
  BUYER_ACCOUNTS: 'Company accounts',
  DRIVER: 'Driver',
  SUPPLIER_ADMIN: 'Pump owner / manager',
  SUPPLIER_ACCOUNTS: 'Pump accounts',
  SUPPLIER_ATTENDANT: 'Pump attendant / cashier',
  PLATFORM_OPERATOR: 'SuperIndent support',
};

export const PERMISSIONS = [
  // buyer side
  'buyer.people.manage',
  'buyer.people.view',
  'buyer.vehicles.manage',
  'buyer.policy.manage',
  'buyer.relationships.manage',
  'buyer.terms.accept',
  'indent.issue',
  'indent.approve',
  'indent.revoke',
  'indent.view.all',
  'indent.request',
  'indent.view.own',
  'indent.cancel.own',
  'ack.own',
  'dispute.own',
  'ack.accounts',
  'buyer.statements.view',
  'buyer.payments.report',
  'buyer.adjustments.approve',
  'buyer.disputes.raise',
  // supplier side
  'supplier.relationships.manage',
  'supplier.terms.manage',
  'supplier.freeze.manage',
  'supplier.locations.manage',
  'supplier.staff.manage',
  'supplier.prices.manage',
  'supplier.paymentDetails.manage',
  'supplier.ledger.view',
  'supplier.statements.issue',
  'supplier.invoices.attach',
  'supplier.payments.confirm',
  'supplier.payments.record',
  'supplier.allocations.manage',
  'supplier.adjustments.propose',
  'supplier.disputes.handle',
  'supplier.openingBalance.manage',
  'supplier.manualSupply.record',
  'supplier.claims.resolve',
  'supplier.deposits.manage',
  'redeem.validate',
  'redeem.claim',
  'redeem.supply',
  'redeem.ack',
  // shared
  'org.members.manage',
  'reports.view',
  'audit.view',
  // platform
  'platform.support',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  BUYER_ADMIN: [
    'buyer.people.manage', 'buyer.people.view', 'buyer.vehicles.manage', 'buyer.policy.manage',
    'buyer.relationships.manage', 'buyer.terms.accept', 'indent.issue', 'indent.approve', 'indent.revoke',
    'indent.view.all', 'buyer.statements.view', 'buyer.payments.report', 'buyer.adjustments.approve',
    'buyer.disputes.raise', 'ack.accounts', 'org.members.manage', 'reports.view', 'audit.view',
  ],
  BUYER_APPROVER: ['buyer.people.view', 'indent.issue', 'indent.approve', 'indent.revoke', 'indent.view.all', 'reports.view'],
  BUYER_ACCOUNTS: [
    'buyer.people.view', 'indent.view.all', 'buyer.statements.view', 'buyer.payments.report',
    'buyer.adjustments.approve', 'buyer.disputes.raise', 'ack.accounts', 'reports.view', 'audit.view',
  ],
  DRIVER: ['indent.request', 'indent.view.own', 'indent.cancel.own', 'ack.own', 'dispute.own'],
  SUPPLIER_ADMIN: [
    'supplier.relationships.manage', 'supplier.terms.manage', 'supplier.freeze.manage', 'supplier.locations.manage',
    'supplier.staff.manage', 'supplier.prices.manage', 'supplier.paymentDetails.manage', 'supplier.ledger.view',
    'supplier.statements.issue', 'supplier.invoices.attach', 'supplier.payments.confirm', 'supplier.payments.record',
    'supplier.allocations.manage', 'supplier.adjustments.propose', 'supplier.disputes.handle',
    'supplier.openingBalance.manage', 'supplier.manualSupply.record', 'supplier.claims.resolve',
    'supplier.deposits.manage', 'org.members.manage', 'reports.view', 'audit.view',
    'redeem.validate', 'redeem.claim', 'redeem.supply', 'redeem.ack',
  ],
  SUPPLIER_ACCOUNTS: [
    'supplier.ledger.view', 'supplier.statements.issue', 'supplier.invoices.attach', 'supplier.payments.confirm',
    'supplier.payments.record', 'supplier.allocations.manage', 'supplier.adjustments.propose',
    'supplier.disputes.handle', 'supplier.openingBalance.manage', 'supplier.manualSupply.record',
    'supplier.claims.resolve', 'supplier.deposits.manage', 'reports.view', 'audit.view',
  ],
  SUPPLIER_ATTENDANT: ['redeem.validate', 'redeem.claim', 'redeem.supply', 'redeem.ack'],
  PLATFORM_OPERATOR: ['platform.support'],
};

export function permissionsFor(roles: readonly Role[]): Set<Permission> {
  const set = new Set<Permission>();
  for (const r of roles) for (const p of ROLE_PERMISSIONS[r] ?? []) set.add(p);
  return set;
}
